/**
 * v2 ListCard - one row on the /c/list/<slug> price-sorted list page
 * (Phase 3, 2026-06-08).
 *
 * Each card represents ONE distinct product (brand + canonical name),
 * showing its cheapest True Final Price across the retailers that
 * carry it. Clicking the product name takes the user to either a
 * persistent /c/<slug> pSEO page (when one already exists for this
 * brand+model) or /compare?q=<canonical_name> (live search; the
 * search-by-query stream auto-publishes the slug on completion via
 * api/app/services/pseo/publisher.py::maybe_publish_product, so every
 * /compare visit also creates the missing pSEO page in the
 * background).
 *
 * 2026-06-12 fix: the previous href pointed at /search?q=... which is
 * not a route in this Next.js app (404). The list page server now
 * resolves the right href per entry via lib/list-entries.ts and
 * passes it through `entry.href`; this component falls back to
 * /compare?q=... when no href is provided (live-search render path).
 *
 * Design constraints:
 *   - Quiet, dense, scannable. The page renders up to 10 of these
 *     stacked vertically; visual noise per card has to be low.
 *   - Numeric TFP is the dominant signal: aligned right, bold.
 *   - No nested CTAs (Buy / Negotiate / Share). Those belong on the
 *     per-product comparison page where the full retailer table
 *     justifies them. List page is for ranking + discovery only.
 *   - No em-dashes, no middle-dots (per user constraints).
 */

import Image from "next/image";
import Link from "next/link";
import type { Route } from "next";

import { formatAUD } from "@/lib/utils";
import { prettifyRetailerSeoName } from "@/lib/retailers/prettify";

export interface ListCardEntry {
  position: number;
  canonical_name: string;
  brand: string;
  /**
   * Manufacturer part / model number for the cheapest product in the
   * group. Used (together with `brand`) to build a clean live-search
   * query for the "Compare retailers" CTA: `brand + mpn` is far less
   * noisy than the retailer's long marketing title, so Phase A
   * discovery's site-search returns matches across many more retailers.
   * Optional because some products carry no usable MPN.
   */
  mpn?: string | null;
  image_url: string | null;
  delivered_total: string;
  retailer_slug: string;
  retailer_count: number;
  /**
   * Pre-resolved destination URL for the "Compare retailers" CTA.
   * - `/c/<slug>` when an existing pSEO product page is known.
   * - `/compare?q=<brand> <mpn>` (clean) when brand+mpn are present
   *   on the entry.
   * - `/compare?q=<canonical_name>` (legacy safe default) otherwise.
   * Optional so live-search renderers (ComparisonStream) can skip the
   * lookup and accept the default fallback.
   */
  href?: string;
}

interface Props {
  entry: ListCardEntry;
}

export function ListCard({ entry }: Props) {
  const {
    position,
    canonical_name,
    brand,
    mpn,
    image_url,
    delivered_total,
    retailer_slug,
    retailer_count,
    href,
  } = entry;

  // 2026-06-13: prefer the clean `brand + mpn` query over the long
  // canonical_name when constructing the fallback /compare URL.
  // canonical_name is often a retailer's marketing title stuffed with
  // spec tokens ("Samsung LS34J550W, 34\" LED LCD Ultra Wide WQHD
  // Freesync Gaming Monitor 3440x1440"); the intent parser captures
  // every spec word and _append_specs_to_target then bakes them all
  // into every per-retailer site-search SERP, narrowing the query so
  // hard that only the retailer who literally has that title in their
  // page text returns a hit. Sending `${brand} ${mpn}` instead keeps
  // Phase A site-searches broad enough to match every retailer that
  // sells the SKU.
  const cleanQuery =
    brand && mpn ? `${brand} ${mpn}` : canonical_name;
  const compareHref = (href ??
    `/compare?q=${encodeURIComponent(cleanQuery)}`) as Route;
  const retailerName = prettifyRetailerSeoName(retailer_slug);
  const offerLine =
    retailer_count > 1
      ? `Cheapest at ${retailerName}, carried by ${retailer_count} retailers`
      : `Available at ${retailerName}`;

  // 2026-06-13: stretched-link pattern. ONE Next.js <Link> (the
  // visible amber CTA on the right) carries an `after:absolute
  // inset-0` pseudo-element that stretches across the whole
  // <article>. Clicking ANYWHERE on the card navigates; only a
  // single focusable / labelled link exists, which is accessible.
  // The title text is rendered as a plain <h3> (no inner anchor) so
  // there are no nested anchors (HTML disallows that).
  return (
    <article className="group relative flex items-stretch gap-4 rounded-2xl border border-border bg-secondary p-4 transition hover:border-border hover:bg-secondary">
      <div className="flex shrink-0 items-center justify-center self-start rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold tabular-nums text-muted-foreground">
        {position}
      </div>

      <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-secondary sm:h-24 sm:w-24">
        {image_url ? (
          <Image
            src={image_url}
            alt={canonical_name}
            fill
            sizes="96px"
            className="object-contain p-1"
            unoptimized
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[12px] uppercase tracking-wider text-muted-foreground">
            No image
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="text-[12px] uppercase tracking-wider text-muted-foreground">
          {brand || "Product"}
        </div>
        <h3 className="mt-0.5 truncate text-base font-semibold leading-tight text-foreground">
          {canonical_name}
        </h3>
        <div className="mt-1 text-sm text-muted-foreground">{offerLine}</div>
      </div>

      <div className="flex shrink-0 flex-col items-end justify-center gap-1 pl-2">
        <div className="text-[12px] uppercase tracking-wider text-muted-foreground">
          True Final Price
        </div>
        <div className="text-lg font-semibold tabular-nums text-foreground">
          {formatAUD(delivered_total)}
        </div>
        <Link
          href={compareHref}
          prefetch={false}
          aria-label={`Buy or negotiate ${canonical_name}`}
          className="relative z-10 inline-flex items-center gap-1 text-xs font-medium text-amber-300/90 hover:text-amber-200 focus:text-amber-200 focus:outline-none after:absolute after:inset-0 after:content-[''] after:rounded-2xl"
        >
          Buy or negotiate
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
}
